import { useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import OpenEye from '../../assets/icons/open_eye.svg'
import InfoBlue from '../../assets/icons/infoblue.svg'
import { setReferableCases, setNonReferableCases } from '../../redux/cases'
import ClosedEye from '../../assets/icons/close_eye.svg'
import InfoGray from '../../assets/icons/infogray.svg'
import ImagesUpload from './ImagesUpload'
import ImagesReceived from './ImagesReceived'
import { stepNotContinue } from '../../redux/steps'
import { useDxStudy } from '../../hooks/NewCase/Service/useDxStudy'
import { resetImages } from '../../redux/images'
import { setEvaluations } from '../../redux/evaluations'
import CancelCaseModal from '../Stepper/CancelCaseModal'
import { useModal } from '../../hooks/useModal'
import GenericModal from '../Modal/GenericModal'
import {
  Container,
  TabsWrapper,
  Tab,
  TabText,
  Icon,
  ButtonsWrapper,
  Button,
  ClinicalTabContainer,
  ClinicalTitle,
  ClinicalGrid,
  ClinicalItem,
  ClinicalLabel,
  ClinicalValue,
  ClinicalTextBlock,
  ClinicalTextValue
} from './styles'
import { setMotivoMalaCalidad } from '../../redux/motivoMalaCalidad'

const formatClinicalValue = (value) => {
  if (value === 0) return '0'
  if (value === null || value === undefined || value === '') return '-'
  return value
}

const formatDate = (value) => {
  if (!value) return '-'
  const normalizedDate = new Date(value)
  if (Number.isNaN(normalizedDate.getTime())) return value
  return normalizedDate.toLocaleDateString('es-AR')
}

const REFERRED_TECHNICIAN_STATES = ['pendiente', 'informando', 'informado']

const parseBooleanDecision = (value) => {
  if (value === true || value === 1 || value === '1') return true
  if (value === false || value === 0 || value === '0') return false
  return null
}

const getTechnicianReferralDecision = (study) => {
  const normalizedState = `${study && study.estado ? study.estado : ''}`.trim().toLowerCase()
  if (!normalizedState) return null
  if (normalizedState === 'no referido') return false
  if (REFERRED_TECHNICIAN_STATES.includes(normalizedState)) return true
  return null
}

const DIAGNOSIS_IN_FLIGHT_STORAGE_KEY = '_dx_in_flight'

export function IAQualityControl ({
  study,
  nextStep,
  updateSelected,
  showClinicalTab = false,
  onTabChange,
  patientData,
  manualImageQuality = {},
  onManualImageQualityChange,
  enableManualQualitySelection = false
}) {
  const { images } = useSelector(state => state)
  const { user } = useSelector(state => state)
  const [modalRef, openModal, closeModal] = useModal()
  const [showQualityModal, setShowQualityModal] = useState(false)

  const shouldRenderClinicalTab = showClinicalTab

  const availableTab = (which) => {
    if (!study) return true
    if (study.tipo === 0) return true
    if (which === 1) return study.tipo === 1
    return study.tipo === 2
  }

  const tabs = useMemo(() => {
    const baseTabs = [
      { id: 0, text: 'Imágenes ojo derecho', available: availableTab(1), showEyeIcon: true },
      { id: 1, text: 'Imágenes ojo izquierdo', available: availableTab(2), showEyeIcon: true }
    ]

    if (shouldRenderClinicalTab) {
      baseTabs.push({ id: 2, text: 'Datos clínicos', available: true, showEyeIcon: false, showClinicalIcon: true })
    }

    return baseTabs
  }, [study && study.tipo, shouldRenderClinicalTab])

  const firstAvailableTab = useMemo(() => tabs.find(tab => tab.available) || tabs[0], [tabs])

  const [selectedTabId, setSelectedTabId] = useState(firstAvailableTab ? firstAvailableTab.id : 0)
  const [emptyImages, setEmptyImages] = useState(true)
  const [loadingImages, setLoadingImages] = useState(false)
  const distpatch = useDispatch()
  const { dxStudy } = useDxStudy()

  useEffect(() => {
    if (!tabs.some(tab => tab.id === selectedTabId && tab.available) && firstAvailableTab) {
      setSelectedTabId(firstAvailableTab.id)
    }
  }, [tabs, firstAvailableTab, selectedTabId])

  useEffect(() => {
    if (updateSelected) {
      updateSelected(selectedTabId)
    }
    if (onTabChange) {
      onTabChange(selectedTabId)
    }
  }, [selectedTabId, updateSelected, onTabChange])

  const noImages = () => {
    if (!study) return true
    const leftHasImages = images.leftEye.images.some(img => img.image)
    const rightHasImages = images.rightEye.images.some(img => img.image)

    if (study.tipo === 0) return !leftHasImages || !rightHasImages
    if (study.tipo === 1) return !rightHasImages
    if (study.tipo === 2) return !leftHasImages
    return true
  }

  const hasPoorQualityImages = () => {
    if (!study) return false
    const leftImages = images.leftEye.images
    const rightImages = images.rightEye.images

    if (study.tipo === 0) {
      return leftImages.some(img => img.status === 0) || rightImages.some(img => img.status === 0)
    } else if (study.tipo === 1) {
      return rightImages.some(img => img.status === 0)
    } else {
      return leftImages.some(img => img.status === 0)
    }
  }

  const hasLoadingImages = () => {
    if (!study) return false
    const leftImages = images.leftEye.images
    const rightImages = images.rightEye.images

    if (study.tipo === 0) {
      return leftImages.some(img => img.status === 'loading') || rightImages.some(img => img.status === 'loading')
    } else if (study.tipo === 1) {
      return rightImages.some(img => img.status === 'loading')
    } else {
      return leftImages.some(img => img.status === 'loading')
    }
  }

  useEffect(() => {
    if (noImages()) {
      setEmptyImages(true)
    } else {
      setEmptyImages(false)
    }
    setLoadingImages(hasLoadingImages())
  }, [images])

  const evaluate = async () => {
    if (!study) return

    try {
      const response = await dxStudy(study.id)

      if (!response) {
        console.warn('[DX] evaluate() — sin respuesta del backend (IA falló o HTTP 500). Señalando error al store.')
        distpatch(setEvaluations({ ...study, estado: 'incompleto' }))
        return
      }

      if (response.estado === 'diagnosticando') {
        return
      }

      if (response.resumen_DX_IA === true) {
        distpatch(setReferableCases())
      } else if (response.resumen_DX_IA === false) {
        distpatch(setNonReferableCases())
      }

      sessionStorage.removeItem(DIAGNOSIS_IN_FLIGHT_STORAGE_KEY)
    } finally {
      distpatch(resetImages())
    }
  }

  const handleSelectTab = (id) => {
    const clickedTab = tabs.find(elem => elem.id === id)
    if (!clickedTab || !clickedTab.available) return
    setSelectedTabId(id)
  }

  const handleDragOver = (event, tab) => {
    event.preventDefault()
    if (!tab.available) return
    if (tab.id !== selectedTabId) {
      setSelectedTabId(tab.id)
    }
  }

  const proceedWithEvaluation = (motivoEnvioMalaCalidad = '') => {
    if (motivoEnvioMalaCalidad) {
      distpatch(setMotivoMalaCalidad(motivoEnvioMalaCalidad))
    }

    if (study && study.id) {
      distpatch(setEvaluations({ ...study, estado: 'diagnosticando' }))
      sessionStorage.setItem(DIAGNOSIS_IN_FLIGHT_STORAGE_KEY, JSON.stringify({
        studyId: study.id,
        startedAt: new Date().toISOString()
      }))
    }

    distpatch(stepNotContinue())
    if (nextStep) {
      nextStep()
    }
    evaluate()
  }

  const handlePressEvaluateButton = () => {
    if (emptyImages || loadingImages) return

    if (hasPoorQualityImages()) {
      setShowQualityModal(true)
      return
    }

    proceedWithEvaluation()
  }

  const handleModalSave = (motivoEnvioMalaCalidad) => {
    if (!motivoEnvioMalaCalidad.trim()) return
    proceedWithEvaluation(motivoEnvioMalaCalidad)
    setShowQualityModal(false)
  }

  const clinicalData = {
    date: formatDate(study && study.fecha),
    aniosConDiabetes: formatClinicalValue((study && study.aniosConDiabetes) ?? (patientData && patientData.aniosConDiabetes)),
    antecedentesGlaucoma: formatClinicalValue((study && study.tieneAntecedentesGlaucoma) ?? (patientData && patientData.tieneAntecedentesGlaucoma)),
    peso: formatClinicalValue((study && study.peso) ?? (patientData && patientData.peso)),
    altura: formatClinicalValue((study && study.altura) ?? (patientData && patientData.altura)),
    informacionAdicionalEstudio: formatClinicalValue(study && study.informacionAdicional),
    informacionAdicionalPaciente: formatClinicalValue(patientData && patientData.informacionAdicional),
    motivoDecisionTecnico: formatClinicalValue(study && study.motivo_decision_tecnico),
    observacionLibreTecnico: formatClinicalValue(study && study.observacion)
  }

  const shouldShowTechnicianReferralReason = useMemo(() => {
    const iaDecision = parseBooleanDecision(study && study.resumen_DX_IA)
    const technicianDecision = getTechnicianReferralDecision(study)

    if (iaDecision === null || technicianDecision === null) return false
    return iaDecision !== technicianDecision
  }, [study])

  return (
    <Container role={user.role}>
      <CancelCaseModal
        ref={modalRef}
        close={closeModal}
      />

      <GenericModal
        isOpen={showQualityModal}
        onClose={() => setShowQualityModal(false)}
        withInput
        title='Imágenes de mala calidad detectadas'
        description='Se han detectado imágenes de mala calidad en este estudio. ¿Está seguro que desea continuar con el diagnóstico?'
        inputTitle='Justificación (requerida):'
        placeholder='Explique por qué desea proceder con imágenes de mala calidad...'
        onSave={handleModalSave}
        saveButtonText='Continuar con el diagnóstico'
        cancelButtonText='Cancelar'
      />

      <TabsWrapper>
        {tabs.map(tab => (
          <Tab
            key={tab.id}
            active={tab.id === selectedTabId}
            available={tab.available}
            tabCount={tabs.length}
            onClick={tab.available ? () => handleSelectTab(tab.id) : null}
            onDragOver={event => handleDragOver(event, tab)}
          >
            <TabText> {tab.text} </TabText>
            {tab.showEyeIcon && (
              <Icon
                src={selectedTabId === tab.id ? OpenEye : ClosedEye}
                alt='icon'
              />
            )}
            {tab.showClinicalIcon && (
              <Icon
                src={selectedTabId === tab.id ? InfoBlue : InfoGray}
                alt='icon'
              />
            )}
          </Tab>
        ))}
      </TabsWrapper>

      {user.role === 'technical' && (
        <>
          <ImagesUpload study={study} whichEye='right' isVisible={selectedTabId === 0} />
          <ImagesUpload study={study} whichEye='left' isVisible={selectedTabId === 1} />
          <ButtonsWrapper>
            <Button type='secondary' onClick={openModal}> Cancelar </Button>
            <Button
              type='primary'
              disabled={emptyImages || loadingImages}
              onClick={handlePressEvaluateButton}
            >
              {loadingImages ? 'Procesando imágenes...' : 'Realizar diagnóstico'}
            </Button>
          </ButtonsWrapper>
        </>
      )}

      {['ophthalmologist', 'patient'].includes(user.role) && (
        <>
          <ImagesReceived
            whichEye='right'
            onlyIAImages={user.role === 'patient'}
            isVisible={selectedTabId === 0}
            dr
            glaucoma={false}
            manualImageQuality={manualImageQuality}
            onManualImageQualityChange={onManualImageQualityChange}
            allowManualQualitySelection={enableManualQualitySelection && user.role === 'ophthalmologist'}
            showIAQualityIndicators={user.role === 'ophthalmologist'}
          />
          <ImagesReceived
            whichEye='left'
            onlyIAImages={user.role === 'patient'}
            isVisible={selectedTabId === 1}
            dr
            glaucoma={false}
            manualImageQuality={manualImageQuality}
            onManualImageQualityChange={onManualImageQualityChange}
            allowManualQualitySelection={enableManualQualitySelection && user.role === 'ophthalmologist'}
            showIAQualityIndicators={user.role === 'ophthalmologist'}
          />

          {shouldRenderClinicalTab && selectedTabId === 2 && (
            <ClinicalTabContainer>
              <ClinicalTitle>Datos clínicos del paciente</ClinicalTitle>

              <ClinicalGrid>
                <ClinicalItem>
                  <ClinicalLabel>Fecha del estudio</ClinicalLabel>
                  <ClinicalValue>{clinicalData.date}</ClinicalValue>
                </ClinicalItem>
                <ClinicalItem>
                  <ClinicalLabel>Años con diabetes</ClinicalLabel>
                  <ClinicalValue>{clinicalData.aniosConDiabetes}</ClinicalValue>
                </ClinicalItem>
                <ClinicalItem>
                  <ClinicalLabel>Antecedentes de glaucoma</ClinicalLabel>
                  <ClinicalValue>{clinicalData.antecedentesGlaucoma}</ClinicalValue>
                </ClinicalItem>
                <ClinicalItem>
                  <ClinicalLabel>Peso / altura</ClinicalLabel>
                  <ClinicalValue>{`${clinicalData.peso} kg / ${clinicalData.altura} cm`}</ClinicalValue>
                </ClinicalItem>
              </ClinicalGrid>

              <ClinicalTextBlock>
                <ClinicalLabel>Información clínica adicional del paciente</ClinicalLabel>
                <ClinicalTextValue>{clinicalData.informacionAdicionalPaciente}</ClinicalTextValue>
              </ClinicalTextBlock>

              <ClinicalTextBlock>
                <ClinicalLabel>Otra información provista por el técnico</ClinicalLabel>
                <ClinicalTextValue>{clinicalData.informacionAdicionalEstudio}</ClinicalTextValue>
              </ClinicalTextBlock>

              {shouldShowTechnicianReferralReason && (
                <ClinicalTextBlock>
                  <ClinicalLabel>Motivo de la referencia por parte del técnico</ClinicalLabel>
                  <ClinicalTextValue>{clinicalData.motivoDecisionTecnico}</ClinicalTextValue>
                </ClinicalTextBlock>
              )}

              {clinicalData.observacionLibreTecnico !== '-' && (
                <ClinicalTextBlock>
                  <ClinicalLabel>Observación libre del técnico</ClinicalLabel>
                  <ClinicalTextValue>{clinicalData.observacionLibreTecnico}</ClinicalTextValue>
                </ClinicalTextBlock>
              )}
            </ClinicalTabContainer>
          )}
        </>
      )}

    </Container>
  )
}

export default IAQualityControl
